import { Modal } from "@wrstudios/components";
import { formatNumber, pluralize } from "@wrstudios/utils";
import React, { useState } from "react";
import Helmet from "react-helmet";
import {
  getAverageDOM,
  getAverageSoldToListPricePercentage
} from "../../../utils/listings";
import Wistia from "../../common/Wistia";
import AgentNote from "../common/AgentNote";
import LiveHeader from "../common/LiveHeader";
import IconInfo from "../icons/IconInfo";
import LiveListingPreview, {
  useListingPreviewResizer
} from "../listing/LiveListingPreview";
import { useLive } from "../LiveProvider";
import LiveListings from "./LiveListings";
import LiveTimeToSellChart from "./LiveTimeToSellChart";
import {
  Bullet,
  Cell,
  Container,
  Content,
  Description,
  Divider,
  DividerCell,
  Grid,
  Info,
  ModalContent,
  ModalText,
  Sidebar,
  SubTitle,
  Title,
  TitleContainer,
  Value
} from "./styled/live-time-to-sell";

function LiveTimeToSell() {
  const { visibleListings, getModuleConfig } = useLive();
  const { headerRef, mobileHeaderRef, previewRef, headerDetails } =
    useListingPreviewResizer();
  const [previewListingId, setPreviewListingId] = useState(null);
  const [hoverListingId, setHoverListingId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const soldListings = visibleListings.filter((listing) => listing.soldPrice);
  const averageSoldDOM = getAverageDOM(soldListings);
  const averageSoldToListPricePercentage = getAverageSoldToListPricePercentage(
    soldListings.filter((listing) => listing.originalListPrice)
  );
  const pageConfig = getModuleConfig("time-to-sell");

  return (
    <>
      <Helmet>
        <title>Time to Sell</title>
      </Helmet>
      <LiveHeader headerRef={headerRef} mobileHeaderRef={mobileHeaderRef}>
        Time to Sell
      </LiveHeader>
      <Container>
        <Sidebar>
          <LiveListings
            previewListingId={previewListingId}
            setPreviewListingId={setPreviewListingId}
            setHoverListingId={setHoverListingId}
            listingFormat={(listing) => (
              <>
                {!!listing.dom && (
                  <>
                    {formatNumber(listing.dom)} {pluralize("Days", listing.dom)}
                  </>
                )}
                {!!listing.dom && !!listing.soldToListPricePercentage && (
                  <Bullet className="group-listing-bullet">
                    &nbsp;&bull;&nbsp;
                  </Bullet>
                )}
                {!!listing.soldToListPricePercentage && (
                  <>
                    {formatNumber(listing.soldToListPricePercentage, "0.[00]")}%
                  </>
                )}
              </>
            )}
          />
        </Sidebar>
        <Content>
          <AgentNote anchor="top" note={pageConfig.note} />
          <TitleContainer>
            <Grid>
              <Cell>
                <Title className="font-primary page-title">
                  {averageSoldDOM ? formatNumber(averageSoldDOM) : "--"}
                  <SubTitle className="page-sub-title">Days on Market</SubTitle>
                </Title>
              </Cell>
              <DividerCell>
                <Divider />
              </DividerCell>
              <Cell>
                <Title className="font-primary page-title">
                  {averageSoldToListPricePercentage
                    ? `${formatNumber(
                        averageSoldToListPricePercentage,
                        "0[.]00"
                      )}%`
                    : "--%"}
                  <SubTitle className="page-sub-title">of list price</SubTitle>
                </Title>
              </Cell>
            </Grid>
          </TitleContainer>
          {(!!averageSoldDOM || !!averageSoldToListPricePercentage) && (
            <Description className="page-description">
              Sold homes were on the market for an average of{" "}
              <Value className="page-description-value">
                {averageSoldDOM ? formatNumber(averageSoldDOM) : "--"}
              </Value>{" "}
              days before they accepted an offer. These homes sold for an
              average of{" "}
              <Value className="page-description-value">
                {averageSoldToListPricePercentage
                  ? `${formatNumber(
                      averageSoldToListPricePercentage,
                      "0[.]00"
                    )}%`
                  : "--%"}
              </Value>{" "}
              of list price.
              <Info onClick={() => setShowInfoModal(true)}>
                <IconInfo />
              </Info>
            </Description>
          )}
          {!averageSoldDOM && !averageSoldToListPricePercentage && (
            <Description className="page-description">
              To view the average days on market and % of list price of sold
              listings, please add sold comparables to this report.
            </Description>
          )}
          <LiveTimeToSellChart
            listings={visibleListings}
            hoverListingId={hoverListingId}
            previewListingId={previewListingId}
            setPreviewListingId={setPreviewListingId}
          />
        </Content>
        {previewListingId && (
          <LiveListingPreview
            ref={previewRef}
            style={{ top: headerDetails.bottom }}
            listingRecordId={previewListingId}
            showInModal={true}
            onClose={() => setPreviewListingId(null)}
          />
        )}
      </Container>
      {showInfoModal && (
        <Modal onClose={() => setShowInfoModal(false)}>
          <ModalContent>
            <Modal.Header className="font-primary modal-header">
              Time to Sell Scatter Chart
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <ModalText className="modal-text">
                The time to sell scatter chart is a way to visualize how long
                the listings took to sell. Not only can you see the relationship
                between the price and and days on market, but you can also see
                the difference between the original list price and the sold
                price. If the listing isn’t sold, you can see if the price has
                dropped.
              </ModalText>
              <Wistia videoId="wso5hd233c" autoplay={false} />
            </Modal.Body>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

export default LiveTimeToSell;
